import * as amplitude from '@amplitude/analytics-browser';

import { DEEPLINK_PARAMS } from '../constants/deeplinkParams';
import { ANALYTICS_EVENTS } from '../constants/events';
import LOCAL_STORAGE_KEY from '../constants/localStorageKey';
import ROUTES from '../constants/routes';
import STORAGE_KEYS from '../constants/storage';
import { getStorage } from '../utils/general.util';

/**
 * Deep-link de campaña a Certificados (t-152): `/certificates/?src=<origen>&c=<campaña>`.
 *
 * Contrato: el enlace lleva al usuario a SUS certificados aunque en medio haya
 * login (OTP) u onboarding completo. El intent se captura al boot (antes del
 * rebote anónimo a /login), sobrevive en storage y lo consumen dos puntos:
 *  - el host de onboarding, que al SALIR navega a `return_to` en vez de home;
 *  - la pantalla de certificados, que dispara `cert_deeplink_completed` y lo borra.
 * Sin `?src=` no se escribe nada: el resto de la app no cambia.
 *
 * `return_to` es siempre «Tus ingresos de Plataformas Digitales»
 * (`/certificates/`, nunca una URL externa), así que no hay redirect abierto.
 * Las rutas retiradas de Rappi y Zubale siguen aceptándose como entrada por los
 * enlaces ya repartidos: redirigen a esa misma pantalla.
 */
export type CertificatesDeeplink = {
	src: string;
	campaign: string | null;
	return_to: string;
	captured_at: number;
};

const CERTIFICATES_ROUTES: readonly string[] = [
	ROUTES.GENERAL_CERTIFICATES,
	ROUTES.RAPPI_CERTIFICATES,
	ROUTES.ZUBALE_CERTIFICATES,
];

const INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const withTrailingSlash = (pathname: string): string =>
	pathname.endsWith('/') ? pathname : `${pathname}/`;

export const clearCertificatesDeeplink = (): void => {
	getStorage().removeItem(STORAGE_KEYS.CERTIFICATES_DEEPLINK);
};

export const readCertificatesDeeplink = (): CertificatesDeeplink | null => {
	const raw = getStorage().getItem(STORAGE_KEYS.CERTIFICATES_DEEPLINK);
	if (!raw) return null;
	try {
		const intent = JSON.parse(raw) as CertificatesDeeplink;
		const expired = Date.now() - intent.captured_at > INTENT_TTL_MS;
		if (expired || !CERTIFICATES_ROUTES.includes(intent.return_to)) {
			clearCertificatesDeeplink();
			return null;
		}
		return intent;
	} catch {
		clearCertificatesDeeplink();
		return null;
	}
};

/** Captura al boot (main.tsx). `cert_deeplink_opened` sale antes de auth: el
 * SDK de Amplitude encola lo que se dispara antes de `init` y lo manda después,
 * así que el clic cuenta aunque el usuario abandone en el OTP. */
export const captureCertificatesDeeplinkFromUrl = (): void => {
	const params = new URLSearchParams(window.location.search);
	const src = params.get(DEEPLINK_PARAMS.SRC);
	if (!src || !CERTIFICATES_ROUTES.includes(withTrailingSlash(window.location.pathname))) return;

	const intent: CertificatesDeeplink = {
		src,
		campaign: params.get(DEEPLINK_PARAMS.CAMPAIGN),
		return_to: ROUTES.GENERAL_CERTIFICATES,
		captured_at: Date.now(),
	};
	getStorage().setItem(
		STORAGE_KEYS.CERTIFICATES_DEEPLINK,
		JSON.stringify(intent),
	);
	amplitude.track(ANALYTICS_EVENTS.CERT_DEEPLINK_OPENED, {
		src: intent.src,
		campaign: intent.campaign,
		return_to: intent.return_to,
		had_session: !!getStorage().getItem(LOCAL_STORAGE_KEY.TOKEN),
	});
};
